import { useMemo, useState } from "react";
import { Plus, Wallet, Home, Truck, Zap, Droplets, Users, MoreHorizontal, Wrench, HardHat, Percent, AlertTriangle, Gift } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fmtDate, uuid, vals } from "@/lib/format";
import { BigButton, Chip, Empty, Field, PageTitle, Stat, inputCls } from "@/components/ui-kit";
import { AttachmentField, openFile } from "@/components/files/FileFields";
import DataGrid from "@/components/DataGrid";
import useIsDesktop from "@/lib/useIsDesktop";
import { Paperclip } from "lucide-react";

const CATS = [
  { k: "rent", icon: Home }, { k: "transport", icon: Truck }, { k: "power", icon: Zap }, { k: "water", icon: Droplets },
  { k: "salaries", icon: Users }, { k: "repairs", icon: Wrench }, { k: "site_work", icon: HardHat }, { k: "commission", icon: Percent },
  { k: "damages", icon: AlertTriangle }, { k: "incentives", icon: Gift }, { k: "misc", icon: MoreHorizontal },
];

export default function Expenses() {
  const { view, t, lang, money, dispatch } = useApp();
  const [cat, setCat] = useState("transport");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [payee, setPayee] = useState("");
  const [staffId, setStaffId] = useState("");
  const [att, setAtt] = useState("");
  const isDesktop = useIsDesktop();
  const staffList = useMemo(() => vals(view.staff).filter((s) => s.active !== false), [view.staff]);
  const list = useMemo(() => vals(view.expenses).sort((a, b) => b.created_at.localeCompare(a.created_at)), [view.expenses]);
  const month = new Date().toISOString().slice(0, 7);
  const monthTotal = list.filter((e) => e.created_at.startsWith(month)).reduce((s, e) => s + e.amount, 0);
  const byCat = useMemo(() => CATS.map(({ k }) => ({ k, v: list.filter((e) => e.category === k && e.created_at.startsWith(month)).reduce((s, e) => s + e.amount, 0) })).filter((x) => x.v > 0), [list, month]);
  const save = () => {
    const worker = staffList.find((s) => s.id === staffId);
    if (dispatch("expense.create", { id: uuid(), category: cat, amount: Number(amount), note: note.trim(), payee: (worker?.name || payee).trim(), worker_id: worker?.id || "", attachment_id: att })) {
      toast.success(t("expense_saved"));
      setAmount(""); setNote(""); setPayee(""); setStaffId(""); setAtt("");
    }
  };
  return (
    <div className="space-y-5">
      <PageTitle testid="expenses-title">{t("expenses")}</PageTitle>
      <Stat label={t("this_month")} value={money(monthTotal)} icon={Wallet} tone="#F0444B" testid="expenses-month-total" />
      {byCat.length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="expenses-by-cat">{byCat.map((x) => <span key={x.k} className="text-xs font-bold px-2 py-1 rounded-full bg-white border border-border">{t(`exp_${x.k}`)} · <span dir="ltr">{money(x.v)}</span></span>)}</div>}
      <section className="rounded-xl bg-white border border-border p-4 space-y-3 card-soft">
        <div className="flex gap-2 flex-wrap">
          {CATS.map(({ k, icon: Icon }) => <Chip key={k} active={cat === k} onClick={() => setCat(k)} data-testid={`expense-cat-${k}`}><Icon className="w-4 h-4" />{t(`exp_${k}`)}</Chip>)}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("amount")}><input dir="ltr" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="expense-amount-input" /></Field>
          <Field label={t("note")}><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="expense-note-input" /></Field>
          {["incentives", "commission", "salaries"].includes(cat) ? (
            <Field label={t("exp_worker")}><select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={inputCls} data-testid="expense-worker-select"><option value="">—</option>{staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          ) : (
            <Field label={t("exp_payee")}><input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder={t("exp_payee_ph")} className={inputCls} data-testid="expense-payee-input" /></Field>
          )}
        </div>
        <AttachmentField value={att} onChange={setAtt} testid="expense-attachment" />
        <BigButton disabled={!(Number(amount) > 0)} onClick={save} className="w-full" data-testid="expense-save-btn"><Plus className="w-5 h-5" />{t("add_expense")}</BigButton>
      </section>
      {!list.length && !isDesktop && <Empty icon={Wallet} text={t("no_history")} />}
      {isDesktop && (
        <DataGrid testid="expenses-grid" name="expenses" rows={list} dateField="created_at"
          columns={[
            { key: "created_at", label: t("date"), get: (e) => fmtDate(e.created_at, lang) }, { key: "category", label: t("category"), get: (e) => t(`exp_${e.category}`) },
            { key: "note", label: t("note") }, { key: "payee", label: t("exp_payee") }, { key: "staff_name", label: t("staff") }, { key: "amount", label: t("amount"), align: "end", sum: true },
          ]}
          actions={(e) => e.attachment_id && <button type="button" onClick={() => openFile(e.attachment_id)} className="w-7 h-7 rounded hover:bg-[#DCE6EE] grid place-items-center" aria-label={t("view")} data-testid="expense-attachment-open"><Paperclip className="w-4 h-4" /></button>} />
      )}
      <div className={isDesktop ? "hidden" : "space-y-2"}>
        {list.map((e) => (
          <div key={e.id} className="rounded-xl bg-white border border-border p-3 flex justify-between items-center" data-testid="expense-row">
            <div><div className="font-bold">{t(`exp_${e.category}`)} {e.payee && <span className="font-normal">→ {e.payee}</span>} {e.note && <span className="font-normal text-muted-foreground">· {e.note}</span>}</div>
              <div className="text-xs text-muted-foreground">{fmtDate(e.created_at, lang)} · {e.staff_name}{e.pending && ` · ${t("pending")}`}</div></div>
            <div className="font-bold text-primary flex items-center gap-2">{e.attachment_id && <button type="button" onClick={() => openFile(e.attachment_id)} aria-label={t("view")}><Paperclip className="w-4 h-4" /></button>}-{money(e.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
